export type CaptureStatus = 'idle' | 'requesting' | 'active' | 'interrupted' | 'error'
export type CaptureMediaState = 'idle' | 'playing' | 'waiting' | 'stalled' | 'paused' | 'muted' | 'unmuted'

export interface CaptureInfo {
  readonly width: number
  readonly height: number
  readonly label: string
}

export class CaptureService extends EventTarget {
  readonly video: HTMLVideoElement
  private stream: MediaStream | null = null
  status: CaptureStatus = 'idle'
  mediaState: CaptureMediaState = 'idle'
  lastError: string | null = null

  constructor() {
    super()
    this.video = document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.autoplay = true
    this.video.addEventListener('playing', () => {
      if (this.stream) this.setMediaState('playing')
    })
    this.video.addEventListener('waiting', () => {
      if (this.stream) this.setMediaState('waiting')
    })
    this.video.addEventListener('stalled', () => {
      if (this.stream) this.setMediaState('stalled')
    })
    this.video.addEventListener('pause', () => {
      if (this.stream) this.setMediaState('paused')
    })
  }

  async selectWindow(): Promise<CaptureInfo> {
    if (!navigator.mediaDevices?.getDisplayMedia) throw new Error('当前浏览器不支持窗口捕获')
    this.stop()
    this.setStatus('requesting')
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30, max: 60 } },
        audio: false,
      })
      const track = stream.getVideoTracks()[0]
      if (!track) throw new Error('未获得视频轨道')
      this.stream = stream
      this.video.srcObject = stream
      track.addEventListener('ended', () => {
        if (this.stream !== stream) return
        this.setStatus('interrupted')
        this.dispatchEvent(new Event('ended'))
      }, { once: true })
      track.addEventListener('mute', () => {
        if (this.stream === stream) this.setMediaState('muted')
      })
      track.addEventListener('unmute', () => {
        if (this.stream === stream) this.setMediaState('unmuted')
      })
      await this.video.play()
      await this.waitForDimensions()
      this.setStatus('active')
      this.setMediaState(track.muted ? 'muted' : 'playing')
      const settings = track.getSettings()
      return {
        width: this.video.videoWidth || settings.width || 0,
        height: this.video.videoHeight || settings.height || 0,
        label: track.label || 'MapleStory',
      }
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error)
      this.setStatus('error')
      throw error
    }
  }

  get mediaStream(): MediaStream | null {
    return this.stream
  }

  async snapshot(): Promise<ImageBitmap> {
    if (this.status !== 'active' || this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) throw new Error('捕获画面尚未就绪')
    return createImageBitmap(this.video)
  }

  stop(): void {
    const stream = this.stream
    this.stream = null
    for (const track of stream?.getTracks() ?? []) track.stop()
    this.video.srcObject = null
    this.setMediaState('idle')
    if (this.status !== 'idle') this.setStatus('idle')
  }

  private setStatus(status: CaptureStatus): void {
    this.status = status
    this.dispatchEvent(new CustomEvent('status', { detail: status }))
  }

  private setMediaState(state: CaptureMediaState): void {
    if (this.mediaState === state) return
    this.mediaState = state
    this.dispatchEvent(new CustomEvent('media-state', { detail: state }))
  }

  private async waitForDimensions(): Promise<void> {
    if (this.video.videoWidth && this.video.videoHeight) return
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('等待捕获画面超时')), 8_000)
      this.video.addEventListener('loadedmetadata', () => {
        window.clearTimeout(timer)
        resolve()
      }, { once: true })
    })
  }
}

export const captureService = new CaptureService()
